import { useMemo } from "react";
import { useLanguage } from "../i18n/LanguageProvider";

const packages = [
  { type: { nl: "Studio / 1 slaapkamer", en: "Studio / 1 bedroom", fr: "Studio / 1 chambre", de: "Studio / 1 Schlafzimmer" }, area: { nl: "tot 50 m²", en: "up to 50 m²", fr: "jusqu'à 50 m²", de: "bis 50 m²" }, net: 250 },
  { type: { nl: "Appartement 2-3 slaapkamers", en: "Apartment 2-3 bedrooms", fr: "Appartement 2-3 chambres", de: "Wohnung 2-3 Schlafzimmer" }, area: { nl: "50-100 m²", en: "50-100 m²", fr: "50-100 m²", de: "50-100 m²" }, net: 375 },
  { type: { nl: "Gezinswoning", en: "Family home", fr: "Maison familiale", de: "Familienhaus" }, area: { nl: "100-150 m²", en: "100-150 m²", fr: "100-150 m²", de: "100-150 m²" }, net: 550 },
  { type: { nl: "Grote woning 5-6 kamers", en: "Large home 5-6 rooms", fr: "Grande maison 5-6 pièces", de: "Großes Haus 5-6 Zimmer" }, area: { nl: "150-250 m²", en: "150-250 m²", fr: "150-250 m²", de: "150-250 m²" }, net: 850 },
  { type: { nl: "Villa of zeer grote woning", en: "Villa or very large home", fr: "Villa ou très grande maison", de: "Villa oder sehr großes Haus" }, area: { nl: "meer dan 250 m²", en: "more than 250 m²", fr: "plus de 250 m²", de: "mehr als 250 m²" }, net: 1250 },
];

const fmt = new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 });

const labels = {
  nl: { colType: "Woningtype", colArea: "Oppervlakte", colNL: "Nederland (incl. 9% btw)", colBE: "België (incl. 21% btw)", colBiz: "Zakelijk (excl. btw)", from: "vanaf", surcharges: "Toeslagen", smoker: "Rokerswoning", smokerVal: "+25%", pets: "Huisdieren (veel haar/geur)", petsVal: "+15%", neglect: "Sterk verwaarloosd", neglectVal: "Prijs op maat", travel: "Voorrijkosten", travelVal: "Gratis binnen 15 km, daarboven € 0,55/km", disclaimer: "Prijzen gelden bij normale vervuiling. In Nederland geldt 9% btw voor schoonmaakwerk in de woning; bij zware vervuiling of specialistisch werk kan 21% van toepassing zijn. In België geldt altijd 21% btw.", caption: "Pakketprijzen opleverschoonmaak na verhuur" },
  en: { colType: "Property type", colArea: "Surface area", colNL: "Netherlands (incl. 9% VAT)", colBE: "Belgium (incl. 21% VAT)", colBiz: "Business (excl. VAT)", from: "from", surcharges: "Surcharges", smoker: "Smoker's home", smokerVal: "+25%", pets: "Pets (heavy hair/odour)", petsVal: "+15%", neglect: "Severely neglected", neglectVal: "Custom price", travel: "Call-out fees", travelVal: "Free within 15 km, above that €0.55/km", disclaimer: "Prices apply to normal soiling. In the Netherlands 9% VAT applies for residential cleaning; 21% may apply for heavy soiling or specialist work. In Belgium 21% VAT always applies.", caption: "Package prices move-out cleaning" },
  fr: { colType: "Type de logement", colArea: "Surface", colNL: "Pays-Bas (TVA 9% incl.)", colBE: "Belgique (TVA 21% incl.)", colBiz: "Professionnel (hors TVA)", from: "à partir de", surcharges: "Suppléments", smoker: "Logement fumeur", smokerVal: "+25%", pets: "Animaux domestiques", petsVal: "+15%", neglect: "Très négligé", neglectVal: "Sur devis", travel: "Frais de déplacement", travelVal: "Gratuit dans un rayon de 15 km, au-delà 0,55 €/km", disclaimer: "Prix valables pour un degré de salissure normal. Aux Pays-Bas, la TVA de 9% s'applique au nettoyage résidentiel ; 21% peut s'appliquer pour les salissures lourdes. En Belgique, la TVA est toujours de 21%.", caption: "Tarifs forfaitaires nettoyage de remise" },
  de: { colType: "Wohnungstyp", colArea: "Fläche", colNL: "Niederlande (inkl. 9% MwSt.)", colBE: "Belgien (inkl. 21% MwSt.)", colBiz: "Geschäftlich (exkl. MwSt.)", from: "ab", surcharges: "Zuschläge", smoker: "Raucherwohnung", smokerVal: "+25%", pets: "Haustiere (viel Haar/Geruch)", petsVal: "+15%", neglect: "Stark vernachlässigt", neglectVal: "Preis auf Anfrage", travel: "Anfahrtskosten", travelVal: "Gratis innerhalb 15 km, darüber 0,55 €/km", disclaimer: "Preise gelten bei normaler Verschmutzung. In den Niederlanden gilt 9% MwSt. für Wohnungsreinigung; bei starker Verschmutzung oder Spezialarbeit kann 21% gelten. In Belgien gilt immer 21% MwSt.", caption: "Paketpreise Übergabereinigung" },
};

export default function PrijstabelNaVerhuur() {
  const { language } = useLanguage();
  const l = labels[language] || labels.nl;

  const rows = useMemo(() =>
    packages.map((p) => ({
      type: p.type[language] || p.type.nl,
      area: p.area[language] || p.area.nl,
      net: p.net,
      nl: p.net * 1.09,
      be: p.net * 1.21,
    })),
    [language]
  );

  return (
    <div data-testid="prijstabel-na-verhuur">
      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm" data-testid="price-table-desktop">
          <caption className="sr-only">{l.caption}</caption>
          <thead>
            <tr className="bg-[#121212] text-left text-[#A1A1AA]">
              <th scope="col" className="px-4 py-3 font-medium">{l.colType}</th>
              <th scope="col" className="px-4 py-3 font-medium">{l.colArea}</th>
              <th scope="col" className="px-4 py-3 font-medium">{l.colNL}</th>
              <th scope="col" className="px-4 py-3 font-medium">{l.colBE}</th>
              <th scope="col" className="px-4 py-3 font-medium">{l.colBiz}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.type} className="border-t border-white/5 hover:bg-white/[0.02]">
                <th scope="row" className="px-4 py-3 text-white font-medium text-left">{r.type}</th>
                <td className="px-4 py-3 text-[#A1A1AA]">{r.area}</td>
                <td className="px-4 py-3 text-[#4DD0E1] font-semibold">{l.from} {fmt.format(r.nl)}</td>
                <td className="px-4 py-3 text-[#4DD0E1] font-semibold">{l.from} {fmt.format(r.be)}</td>
                <td className="px-4 py-3 text-[#A1A1AA]">{l.from} {fmt.format(r.net)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-4" data-testid="price-cards-mobile">
        {rows.map((r) => (
          <div key={r.type} className="p-4 rounded-xl bg-[#121212] border border-white/10">
            <h4 className="text-white font-bold mb-1">{r.type}</h4>
            <p className="text-xs text-[#A1A1AA] mb-3">{r.area}</p>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-[#A1A1AA]">{l.colNL}</dt>
                <dd className="text-[#4DD0E1] font-semibold">{l.from} {fmt.format(r.nl)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-[#A1A1AA]">{l.colBE}</dt>
                <dd className="text-[#4DD0E1] font-semibold">{l.from} {fmt.format(r.be)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-[#A1A1AA]">{l.colBiz}</dt>
                <dd className="text-[#A1A1AA]">{l.from} {fmt.format(r.net)}</dd>
              </div>
            </dl>
          </div>
        ))}
      </div>

      {/* Surcharges */}
      <div className="mt-8 p-5 rounded-xl bg-[#121212] border border-white/10" data-testid="surcharges">
        <h4 className="text-white font-bold mb-3">{l.surcharges}</h4>
        <dl className="space-y-2 text-sm">
          {[
            [l.smoker, l.smokerVal],
            [l.pets, l.petsVal],
            [l.neglect, l.neglectVal],
            [l.travel, l.travelVal],
          ].map(([dt, dd]) => (
            <div key={dt} className="flex justify-between">
              <dt className="text-[#A1A1AA]">{dt}</dt>
              <dd className="text-white font-medium">{dd}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Disclaimer */}
      <p className="mt-4 text-xs text-[#A1A1AA] leading-relaxed" data-testid="price-disclaimer">{l.disclaimer}</p>
    </div>
  );
}
